import { useContext, useEffect, useMemo, useRef } from "react"

import { useApolloClient } from "@apollo/client"
import debounce from "lodash-es/debounce"

import { evictProjectPrice } from "shared/api/price/evictPriceCache"
import { ProjectPriceDocument } from "shared/api/price/queries.generated"
import { noop } from "shared/lib/noop"
import { useEvent } from "shared/lib/useEvent"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"

import {
  ProjectDocument,
  useProjectChildrenQuery,
  useProjectPermissionsLazyQuery
} from "entities/projects/graphql/queries.generated"
import { useProjectState } from "entities/projects/model/useProjectState"
import { useTeamQuery } from "entities/teams/graphql/queries.generated"
import { getActiveQueries } from "features/project-collaboration/lib/getActiveQueries"
import {
  ProjectCollaborationRefreshPlan,
  mergeProjectCollaborationRefreshPlans,
  planProjectCollaborationRefresh
} from "features/project-collaboration/lib/planProjectCollaborationRefresh"
import {
  ProjectCollaborationMessage,
  ProjectCollaborationMessageType
} from "features/project-collaboration/types/ProjectCollaborationEvent"
import { useProjectStore } from "features/project-management/model/useProjectStore"

import { ProjectCollaborationContext } from "../types/ProjectCollaborationContext"

interface UseProjectCollaborationArgs {
  projectID: string
  subscriberID: string
}

// One user action can publish one message per entity it touched — duplicating a room sends
// one for the room and one for every position in it. Refreshing on the first message of such
// a burst is worse than useless: Apollo answers the refetches the later messages trigger from
// the request the first one already started, so the whole burst ends up reflecting the state
// halfway through the change and nothing asks again. Refreshing on the trailing edge means
// the message that sees the finished state is the one that gets to read it.
const REFRESH_DELAY = 400
// A long stream of messages — another session importing a CSV — would otherwise postpone the
// refresh for as long as it runs.
const MAX_REFRESH_DELAY = 2000

const ATTACHMENT_QUERY_NAMES = ["Attachments", "AttachmentEntities"]

export function useProjectCollaboration({
  projectID,
  subscriberID
}: UseProjectCollaborationArgs) {
  const client = useApolloClient()
  const { pendingElementIDs } = useProjectStore(projectID)
  const { isPending } = useProjectState(projectID)
  const { data: projectData, refetch } = useProjectChildrenQuery({
    variables: { id: projectID },
    skip: !projectID || isPending,
    nextFetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })

  const { refetch: refetchTeam } = useTeamQuery({
    variables: { id: projectData?.project?.team?.id || "" },
    skip:
      !projectID ||
      !projectData?.project?.team?.id ||
      pendingElementIDs.length > 0,
    nextFetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })

  const [refetchProjectPermissions] = useProjectPermissionsLazyQuery({
    variables: { projectID },
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })

  // Evicting the cached price makes the watching query request it again, but Apollo serves an
  // identical request that is still in flight from its deduplication registry, and the price
  // endpoint is slow enough that one change is regularly still loading when the next arrives —
  // so that reply can predate this message. Asking for it separately is the only way to get a
  // read that is guaranteed to start after the change we were just told about. A one-off
  // `client.query` instead of a lazy query, because an executed lazy query keeps watching and
  // would count as someone looking at the price.
  const fetchProjectPrice = useEvent(() => {
    client
      .query({
        query: ProjectPriceDocument,
        variables: { id: projectID },
        fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY,
        context: { queryDeduplication: false }
      })
      .catch(noop)
  })

  const fetchProject = useEvent(() => {
    client
      .query({
        query: ProjectDocument,
        variables: { id: projectID },
        fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
      })
      .catch(noop)
  })

  const pendingPlanRef = useRef<ProjectCollaborationRefreshPlan | null>(null)

  const refresh = useEvent(() => {
    const plan = pendingPlanRef.current
    if (isPending || !plan) return
    pendingPlanRef.current = null

    if (plan.subtree) refetch()
    else if (plan.project) fetchProject()

    if (plan.project) {
      refetchProjectPermissions({})
      if (projectData?.project?.team?.id && pendingElementIDs.length === 0) {
        setTimeout(() => refetchTeam(), 400)
      }
    }

    if (plan.price) {
      evictProjectPrice(projectID, client.cache, true)
      if (getActiveQueries(client, ["ProjectPrice"], { id: projectID }).length)
        fetchProjectPrice()
    }

    if (plan.attachments) {
      getActiveQueries(client, ATTACHMENT_QUERY_NAMES).forEach(query =>
        query.refetch().catch(noop)
      )
    }
  })

  // Background tabs only remember that something changed and catch up once they are visible.
  const debouncedRefresh = useMemo(
    () =>
      debounce(
        () => {
          if (document.visibilityState === "hidden") return
          refresh()
        },
        REFRESH_DELAY,
        { maxWait: MAX_REFRESH_DELAY }
      ),
    [refresh]
  )

  const handleMessage = useEvent((message: ProjectCollaborationMessage) => {
    if (message.message !== ProjectCollaborationMessageType.UPDATE) return
    pendingPlanRef.current = mergeProjectCollaborationRefreshPlans(
      pendingPlanRef.current,
      planProjectCollaborationRefresh(message.changes)
    )
    debouncedRefresh()
  })

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refresh()
    }
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [refresh])

  const { subscribe } = useContext(ProjectCollaborationContext)

  useEffect(() => {
    if (isPending) return noop
    const unsubscribe = subscribe(projectID, subscriberID, {
      onMessage: handleMessage
    })

    return () => {
      unsubscribe?.()
      debouncedRefresh.cancel()
    }
  }, [
    projectID,
    subscriberID,
    isPending,
    subscribe,
    handleMessage,
    debouncedRefresh
  ])
}
